class IconAnimationController {
    constructor() {
        this.animationObserver = null;
        this.animatedIcons = new Set();
        this.iconCounter = 0;
        this.scrollAnimatedIcons = new Set();
        this.isScrollListening = false;
        this.init();
    }

    init() {
        if (!('IntersectionObserver' in window)) {
            console.warn('IntersectionObserver not supported - CSS animations will not be triggered by viewport');
        } else {
            this.setupAnimationObserver();
        }
        
        this.setupScrollListener();
        this.observeIcons();
    }

    setupAnimationObserver() {
        const options = {
            root: null,
            rootMargin: '20px',
            threshold: 0.3
        };

        this.animationObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const iconKey = this.getIconKey(entry.target);
                    
                    if (!this.animatedIcons.has(iconKey)) {
                        this.triggerAnimation(entry.target);
                        this.animatedIcons.add(iconKey);
                        this.animationObserver.unobserve(entry.target);
                    }
                }
            });
        }, options);
    }

    setupScrollListener() {
        if (this.isScrollListening) return;
        
        let ticking = false;
        
        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    this.handleScrollAnimations();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        this.isScrollListening = true;
    }

    handleScrollAnimations() {
        const scrollAnimatedIcons = document.querySelectorAll('img[data-animation-trigger="scroll"]:not(.animation-completed)');
        const viewportHeight = window.innerHeight;
        
        scrollAnimatedIcons.forEach(icon => {
            const iconKey = this.getIconKey(icon);
            
            if (!this.scrollAnimatedIcons.has(iconKey)) {
                const rect = icon.getBoundingClientRect();
                
                // Sprawdź czy dolna część viewport dotknęła górnej części elementu
                if (rect.top <= viewportHeight && rect.bottom >= 0) {
                    this.triggerAnimation(icon);
                    this.scrollAnimatedIcons.add(iconKey);
                }
            }
        });
    }

    getIconKey(img) {
        if (!img.dataset.iconAnimationId) {
            img.dataset.iconAnimationId = `icon_${++this.iconCounter}_${Date.now()}`;
        }
        
        return img.dataset.iconAnimationId;
    }

    triggerAnimation(img) {
        // Cache busting hack - wymusza ponowne zaÅ‚adowanie SVG z animacjami
        if (img.src && img.src.includes('.svg')) {
            const originalSrc = img.src.split('?')[0];
            const cacheBustedSrc = `${originalSrc}?_t=${Date.now()}`;
            
            // Zachowaj opacity podczas przeÅ‚Ä…czania
            img.style.transition = 'opacity 0.1s ease';
            img.style.opacity = '0';
            
            img.onload = () => {
                img.style.opacity = '1';
                img.classList.add('animation-completed');
            };
            
            img.onerror = () => {
                img.style.opacity = '1';
                img.src = originalSrc;
                img.classList.add('animation-completed');
            };
            
            img.src = cacheBustedSrc;
        } else {
            img.classList.add('animation-completed');
        }
    }

    hasAnimationElements(img) {
        const directElements = img.querySelectorAll('.outline, .fill');
        if (directElements.length > 0) {
            return true;
        }

        const parentSvg = img.closest('svg');
        if (parentSvg && parentSvg.querySelector('.outline, .fill')) {
            return true;
        }

        // FIXED: SprawdÅº zarÃ³wno ikony jak i ilustracje
        if (img.src && (img.src.includes('/icons/') || img.src.includes('/illustrations/')) && img.classList.contains('icon-animated')) {
            return true;
        }

        return false;
    }

    observeIcons() {
        // Observe ikony z klasÄ… .icon-animated
        const animatedIcons = document.querySelectorAll('img.icon-animated:not(.animation-completed)');
        animatedIcons.forEach(icon => {
            const iconKey = this.getIconKey(icon);
            
            if (!this.animatedIcons.has(iconKey)) {
                if (icon.complete) {
                    this.checkAndObserveIcon(icon);
                } else {
                    icon.onload = () => {
                        this.checkAndObserveIcon(icon);
                    };
                }
            }
        });

        // FIXED: SprawdÅº zarÃ³wno ikony jak i ilustracje
        const potentialAnimatedIcons = document.querySelectorAll('img[src*="/icons/"]:not(.animation-completed), img[src*="/illustrations/"]:not(.animation-completed)');
        potentialAnimatedIcons.forEach(icon => {
            const iconKey = this.getIconKey(icon);
            
            if (!icon.classList.contains('icon-animated') && !this.animatedIcons.has(iconKey)) {
                if (this.hasAnimationElements(icon)) {
                    this.checkAndObserveIcon(icon);
                }
            }
        });
    }

    checkAndObserveIcon(icon) {
        if (!this.hasAnimationElements(icon)) return;
        
        const triggerMode = icon.dataset.animationTrigger || 'viewport';
        
        switch (triggerMode) {
            case 'viewport':
                if (this.animationObserver) {
                    this.animationObserver.observe(icon);
                }
                break;
            case 'scroll':
                // Scroll animacje są obsługiwane w handleScrollAnimations
                // nie potrzebujemy tutaj nic robić
                break;
            case 'none':
                // Brak automatycznego triggera
                break;
            default:
                // Domyślnie viewport
                if (this.animationObserver) {
                    this.animationObserver.observe(icon);
                }
        }
    }

    refreshObserver() {
        if (this.animationObserver) {
            this.observeIcons();
        }
    }

    resetIcon(img) {
        const iconKey = this.getIconKey(img);
        this.animatedIcons.delete(iconKey);
        this.scrollAnimatedIcons.delete(iconKey);
        img.classList.remove('animation-completed');
        delete img.dataset.iconAnimationId;
        this.checkAndObserveIcon(img);
    }

    resetAllIcons() {
        this.animatedIcons.clear();
        this.scrollAnimatedIcons.clear();
        this.iconCounter = 0;
        document.querySelectorAll('.animation-completed').forEach(img => {
            img.classList.remove('animation-completed');
            delete img.dataset.iconAnimationId;
        });
        this.refreshObserver();
    }

    destroy() {
        if (this.animationObserver) {
            this.animationObserver.disconnect();
        }
    }
}

export const initializeIconAnimations = () => {
    if (!window.iconAnimationController) {
        window.iconAnimationController = new IconAnimationController();
    }
    
    const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
            mutation.addedNodes.forEach(node => {
                if (node.nodeType === 1) {
                    // FIXED: SprawdÅº zarÃ³wno ikony jak i ilustracje
                    const newIcons = node.querySelectorAll ? 
                        node.querySelectorAll('img.icon-animated, img[src*="/icons/"], img[src*="/illustrations/"]') : [];
                    
                    if (newIcons.length > 0 && window.iconAnimationController) {
                        setTimeout(() => {
                            window.iconAnimationController.refreshObserver();
                        }, 100);
                    }
                }
            });
        });
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    return window.iconAnimationController;
};